import { useMutation, useQuery } from '@apollo/client';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Divider,
  Grid,
  Typography
} from '@mui/material';
import {
  Event,
  CombinedSemester,
  OrganizerType,
  ResolvedEvent,
  WaitingListType,
  EventWaitingType
} from '@thro-types/events';

import { useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import CANCEL_MUTATION from 'gql/mutations/EventService/cancelEvent.gql';
import CREATE_ARTICLE_MUTATION from 'gql/mutations/EventService/createEventArticle.gql';
import DELETE_EVENT_MUTATION from 'gql/mutations/EventService/deleteEvent.gql';
import AUTHOR_ROLE_QUERY from 'gql/queries/EventService/getPermissions.gql';
import USERS_BY_EMAIL_QUERY from 'gql/queries/EventService/getUsersEmail.gql';

import {
  ConfirmationDialog,
  DateBox,
  EntryWithHeadline,
  ErrorBoundary,
  GQLGuard
} from 'app/components';
import { handleDownload } from 'app/components/Util/downloadAttachement';
import { WatchlistAddButton } from 'app/components/WatchlistAddButton';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { UserType } from 'utils/ACLHelper';
import { useAuthContext } from 'utils/hooks/useAuthContext';

import { EventModal } from '../../EventModal';
import { InfoDialog } from '../../components/InfoDialog';
import { OrganizerFabActions, ParticipantFabActions } from '../../components/actions';
import { getTemplateEvent, isEventActive, isPreCalc } from '../../helper';
import '../styles/EventDetailView.scss';

interface Props {
  eventData: ResolvedEvent;
  canEdit: boolean;
  setEnrollingState: (state: 'ENROLLING' | 'UNENROLLING') => void;
  setEnrollingType: (type: WaitingListType) => void;
  readOnlyAdmin: boolean;
  refetch: () => void;
}
interface UserQueryResponse {
  id: number;
  email: string;
}

export const EventOrganizationInfo = ({
  eventData,
  canEdit,
  setEnrollingState,
  setEnrollingType,
  readOnlyAdmin,
  refetch
}: Props) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuthContext();
  const [descriptionAccordionOpen, setDescriptionAccordionOpen] = useState<boolean>(false);
  const [participantCriteriaAccordionOpen, setParticipantCriteriaAccordionOpen] =
    useState<boolean>(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [createArticleDialogOpen, setCreateArticleDialogOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [createInfoDialogOpen, setCreateInfoDialogOpen] = useState(false);
  const [omittedEvent, setOmittedEvent] = useState<ResolvedEvent | undefined>(undefined);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  function participantPosition() {
    // TODO Position, Group and WaitingList for PreCalculated Result

    if (eventData.enrollmentId !== null) {
      if (isPreCalc(eventData) && eventData.groups) {
        let index = 0;
        for (let group of eventData.groups) {
          const preCalcEnrolled = !!group.preCalcEnrollments?.filter(
            pce => pce.id === eventData.enrollmentId
          ).length;

          if (preCalcEnrolled) {
            return { position: undefined, group: index + 1, status: 'participant' };
          }
          index++;
        }
      }

      if (eventData.waitingType === EventWaitingType.EVENT) {
        const position = eventData.waitingList?.enrollments?.findIndex(
          enrollment => enrollment.id === eventData.enrollmentId
        );
        if (position !== undefined && position !== -1) {
          return { position: position + 1, group: undefined, status: 'waitingList' };
        } else {
          if (eventData.groups === undefined) {
            return { position: undefined, group: undefined, status: undefined };
          }
          let index = 0;
          for (let group of eventData.groups) {
            const position = group.enrollments?.findIndex(
              enrollment => enrollment.id === eventData.enrollmentId
            );
            if (position !== undefined && position !== -1) {
              return { position: position + 1, group: index + 1, status: 'participant' };
            }
            index++;
          }
        }
      } else {
        if (eventData.groups === undefined) {
          return { position: undefined, group: undefined, status: undefined };
        }
        let index = 0;
        for (let group of eventData.groups) {
          if (eventData.waitingType === EventWaitingType.GROUP) {
            const position = group.waitingList?.enrollments?.findIndex(
              enrollment => enrollment.id === eventData.enrollmentId
            );
            if (position !== undefined && position !== -1) {
              return { position: position + 1, group: index + 1, status: 'waitingList' };
            }
          }
          const position = group.enrollments?.findIndex(
            enrollment => enrollment.id === eventData.enrollmentId
          );
          if (position !== undefined && position !== -1) {
            return { position: position + 1, group: index + 1, status: 'participant' };
          }
          index++;
        }
      }
    }
    return { position: undefined, group: undefined, status: undefined };
  }
  const { position, group, status } = participantPosition();

  const organizerIds =
    eventData.organizers
      ?.filter(organizer => organizer.organizerType === OrganizerType.USER)
      .map(organizer => organizer.refId) || [];

  const participantIds: number[] =
    eventData.groups?.flatMap(g => {
      return (
        g.enrollments?.map(e => {
          return e.userId;
        }) || []
      );
    }) || [];

  const preCalculatedParticipantIds: number[] =
    eventData.groups?.flatMap(g => {
      return (
        g.preCalcEnrollments?.map(e => {
          return e.userId;
        }) || []
      );
    }) || [];

  const waitingListUserIds = [
    ...(eventData.groups?.flatMap(g => {
      return (
        g.waitingList?.enrollments?.map(e => {
          return e.userId;
        }) || []
      );
    }) || []),
    ...(eventData.waitingList?.enrollments?.map(e => {
      return e.userId;
    }) || [])
  ];

  const preCalcWaitingListUserIds = [
    ...(eventData.groups?.flatMap(g => {
      return (
        g.waitingList?.enrollments
          ?.filter(e => !e.preCalcGroupId)
          .map(e => {
            return e.userId;
          }) || []
      );
    }) || []),
    ...(eventData.waitingList?.enrollments
      ?.filter(e => !e.preCalcGroupId)
      .map(e => {
        return e.userId;
      }) || [])
  ];

  const [deleteEvent] = useMutation(DELETE_EVENT_MUTATION, {
    context: { clientName: 'federation' }
  });

  const [cancelEvent] = useMutation(CANCEL_MUTATION, {
    context: { clientName: 'federation' }
  });

  const [createArticle] = useMutation(CREATE_ARTICLE_MUTATION, {
    context: { clientName: 'federation' }
  });

  const getUsersQuery = useQuery<
    { users: UserQueryResponse[] },
    { ids?: number[]; filter?: string; type?: UserType }
  >(USERS_BY_EMAIL_QUERY, {
    context: { clientName: 'federation' },
    variables: {
      ids: [
        ...organizerIds,
        ...participantIds,
        ...preCalculatedParticipantIds,
        ...waitingListUserIds
      ],
      type: UserType.SHIBBOLETH
    }
  });
  const getAuthorRolesQuery = useQuery(AUTHOR_ROLE_QUERY, { variables: { limit: 0 } });

  const cancelEventCreation = (event, reason) => {
    if (reason && reason === 'backdropClick') {
      return;
    }
    if (disabledCancel) {
      return;
    }
    setFormModalOpen(false);
    setDisabledCancel(false);
    setOmittedEvent(undefined);
  };

  const handleDeleteEvent = async () => {
    try {
      await deleteEvent({
        variables: {
          id: eventData.id
        },
        context: { clientName: 'federation' },
        refetchQueries: ['GetEvents']
      });
      dispatch(AlertActions.success(t(translations.pages.events.alerts.successfulDelete())));
      navigate('/events', { replace: true });
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.events.alerts.failedDelete())));
    }
  };

  const handleCancelEvent = async (event: Event | undefined) => {
    try {
      if (event) {
        await cancelEvent({ variables: { id: event.id }, refetchQueries: ['GetEvents'] });
        dispatch(AlertActions.success(t(translations.pages.events.alerts.successfulCancel())));
        navigate('/events', { replace: true });
      }
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.events.alerts.failedCancel())));
    }
  };

  const handleArticleCreation = async (event: Event | undefined) => {
    try {
      if (event) {
        const { data } = await createArticle({
          variables: { id: event.id },
          refetchQueries: ['GetEvent']
        });
        dispatch(
          AlertActions.success(t(translations.pages.events.alerts.successfulArticleCreation()))
        );
        navigate(`/articles/${data.createEventArticle.articleId}`);
      }
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.events.alerts.failedArticleCreation())));
    }
  };

  const handleMailing = (
    userData: { users: [{ email: string; id: number }] },
    type: 'PARTICIPATING' | 'PRECALC_PARTICIPATING' | 'WAITING' | 'PRECALC_WAITING'
  ) => {
    let receivers: string[] = [];
    switch (type) {
      case 'PARTICIPATING':
        receivers = userData.users
          .filter(user => participantIds.includes(user.id))
          .map(p => p.email);
        break;
      case 'PRECALC_PARTICIPATING':
        receivers = userData.users
          .filter(user => preCalculatedParticipantIds.includes(user.id))
          .map(p => p.email);
        break;
      case 'WAITING':
        receivers = userData.users
          .filter(user => waitingListUserIds.includes(user.id))
          .map(p => p.email);
        break;
      case 'PRECALC_WAITING':
        receivers = userData.users
          .filter(user => preCalcWaitingListUserIds.includes(user.id))
          .map(p => p.email);
        break;
    }
    window.location.href = `mailto:${currentUser.email}?bcc=${receivers.join(', ')}&subject=${
      eventData.name
    }`;
  };

  const handleExportEvent = (exportSignatureList: boolean) => {
    return handleDownload(
      `${eventData.name}${exportSignatureList ? '__Unterschriftenliste' : ''}.xlsx`,
      `/export/events/${eventData.id}/${exportSignatureList ? 'signatureList' : 'signedIn'}`
    );
  };

  const confirmCreateInfo = () => {
    //Todo open create module with event data
    setOmittedEvent(getTemplateEvent(eventData));
    setFormModalOpen && setFormModalOpen(true);
    setCreateInfoDialogOpen(false);
  };

  return (
    <>
      <InfoDialog
        dialogOpen={createInfoDialogOpen}
        handleConfirm={confirmCreateInfo}
        handleClose={() => setCreateInfoDialogOpen(false)}
      />
      <ErrorBoundary boundaryTag="eventDetailBoundary">
        <EventModal
          event={omittedEvent ?? eventData}
          createModalOpen={formModalOpen}
          disabledCancel={disabledCancel}
          onCancel={cancelEventCreation}
          closeHandler={() => setFormModalOpen(false)}
        />
      </ErrorBoundary>
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => handleDeleteEvent()}
        title={t(translations.pages.events.confirmationDialog.deleteEventWarning())}
      >
        {t(translations.pages.events.confirmationDialog.deleteEventInformation())}
      </ConfirmationDialog>
      <ConfirmationDialog
        open={cancelDialogOpen}
        title={t(translations.pages.events.confirmationDialog.cancelEventWarning())}
        onClose={() => {
          setCancelDialogOpen(false);
        }}
        onConfirm={() => {
          handleCancelEvent(eventData);
          setCancelDialogOpen(false);
        }}
      >
        <div>{t(translations.pages.events.confirmationDialog.cancelEventInformation())}</div>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={createArticleDialogOpen}
        title={t(translations.pages.events.confirmationDialog.createArticleTitle())}
        onClose={() => {
          setCreateArticleDialogOpen(false);
        }}
        onConfirm={() => {
          handleArticleCreation(eventData);
          setCreateArticleDialogOpen(false);
        }}
      >
        <div>{t(translations.pages.events.confirmationDialog.createArticleText())}</div>
      </ConfirmationDialog>
      <GQLGuard query={[getAuthorRolesQuery, getUsersQuery]}>
        {([authorRoles, userData]) => (
          <Grid item className="event-organization">
            <Grid item className="event-organization__information">
              {isMobile ? (
                <Grid container direction={'row'} justifyContent={'space-between'} gap={1}>
                  <Typography component="h2" paddingY={1}>
                    {eventData.name}
                  </Typography>
                  <ParticipantFabActions
                    event={eventData}
                    handleEnroll={() => {
                      setEnrollingState && setEnrollingState('ENROLLING');
                      setEnrollingType(WaitingListType.EVENT);
                    }}
                    handleUnenroll={() => {
                      setEnrollingState && setEnrollingState('UNENROLLING');
                      setEnrollingType(WaitingListType.EVENT);
                    }}
                  />
                </Grid>
              ) : (
                <Grid container direction={'row'} justifyContent={'space-between'} gap={1}>
                  <Grid item xs>
                    <Typography component="h2">
                      {t(translations.pages.events.eventDetailPage.eventInformation())}
                    </Typography>
                  </Grid>
                  <Grid container item xs alignItems={'center'} justifyContent={'flex-end'} gap={1}>
                    <WatchlistAddButton
                      categoryName="EVENT"
                      description={eventData.description}
                      title={eventData.name}
                      date={eventData.startDate}
                      // eventButton
                    />
                    {canEdit ? (
                      <div style={{ paddingBottom: '0.5em' }}>
                        <OrganizerFabActions
                          handleMailClick={type => handleMailing(userData, type)}
                          handleEditClick={() => setFormModalOpen(true)}
                          handleDeleteClick={() => setDeleteDialogOpen(true)}
                          handleCancelClick={() => setCancelDialogOpen(true)}
                          handleArticleClick={() =>
                            eventData.articleId
                              ? navigate(`/articles/${eventData.articleId}`)
                              : setCreateArticleDialogOpen(true)
                          }
                          handleCopyClick={() => setCreateInfoDialogOpen(true)}
                          handleEventExportClick={handleExportEvent}
                          hasEnrollments={eventData.occupancy > 0}
                          isActive={isEventActive(eventData)}
                          attachments={eventData.attachments}
                          refetch={refetch}
                          hasArticle={!!eventData.articleId}
                          isPreCalc={isPreCalc(eventData)}
                        />
                      </div>
                    ) : (
                      <ParticipantFabActions
                        event={eventData}
                        handleEnroll={() => {
                          setEnrollingState && setEnrollingState('ENROLLING');
                          setEnrollingType(WaitingListType.EVENT);
                        }}
                        handleUnenroll={() => {
                          setEnrollingState && setEnrollingState('UNENROLLING');
                          setEnrollingType(WaitingListType.EVENT);
                        }}
                      />
                    )}
                  </Grid>
                </Grid>
              )}
              <Divider style={{ marginBottom: '1rem' }} />
              <Grid container direction={'row'} gap={1}>
                <Grid
                  item
                  xs={12}
                  md={3}
                  className="event-organization__information__headline-datebox"
                >
                  <Typography component={'h3'}>
                    {t(translations.pages.events.detailTitle())}
                  </Typography>
                  <DateBox endDate={eventData.endDate} startDate={eventData.startDate} />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={3}
                  className="event-organization__information__headline-datebox"
                >
                  <Typography component={'h3'}>
                    {t(translations.pages.events.registration())}
                  </Typography>
                  <DateBox
                    endDate={eventData.registrationPeriodEnd}
                    endText={t(translations.pages.events.eventDetailPage.registrationUntil())}
                    startDate={eventData.registrationPeriodStart}
                    startText={t(translations.pages.events.eventDetailPage.registrationFrom())}
                    icon={
                      <PendingActionsIcon className="event-organization__information__customDateBoxIcon" />
                    }
                  />
                </Grid>
                <Grid
                  item
                  container
                  xs={12}
                  md
                  className="event-organization__information__visibilityIcon"
                  alignItems={'center'}
                  marginY={isMobile ? 1 : '2em'}
                >
                  <Grid item xs={0} md />
                  <Grid item>
                    <VisibilityIcon style={{ fontSize: '3em' }} />
                  </Grid>
                  <Grid item>
                    <EntryWithHeadline
                      entry={
                        eventData.visibleFrom
                          ? new Date(eventData.visibleFrom).toLocaleDateString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: 'numeric'
                            })
                          : '-/-'
                      }
                      headline={t(translations.pages.events.create.visibleFrom())}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={4} className="event-organization__information__headline-entry">
                  <EntryWithHeadline
                    entry={eventData.host}
                    headline={t(translations.pages.events.eventDetailPage.hostedBy())}
                  />
                </Grid>
                {canEdit && (
                  <>
                    <Grid item xs={4} className="event-organization__information__headline-entry">
                      <OrganizerRoles eventData={eventData} authorRoles={authorRoles} />
                    </Grid>
                    <Grid item xs={4} className="event-organization__information__headline-entry">
                      <OrganizerUsers
                        userData={userData.users.filter(user => organizerIds.includes(user.id))}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} className="event-organization__information__headline-entry">
                  <Accordion
                    expanded={descriptionAccordionOpen}
                    onChange={() => setDescriptionAccordionOpen(old => !old)}
                  >
                    <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
                      {t(translations.pages.events.create.description())}
                    </AccordionSummary>
                    <Divider />
                    <AccordionDetails>
                      <ReactQuill value={eventData.description} theme="bubble" readOnly />
                    </AccordionDetails>
                  </Accordion>
                </Grid>
                <Grid item xs={12} className="event-organization__information__headline-entry">
                  <Accordion
                    expanded={participantCriteriaAccordionOpen}
                    onChange={() => setParticipantCriteriaAccordionOpen(old => !old)}
                  >
                    <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
                      {t(translations.pages.events.eventDetailPage.admissionCriteria())}
                    </AccordionSummary>
                    <Divider />
                    <AccordionDetails>
                      {
                        <Grid container spacing={2}>
                          <Grid item container>
                            <Grid
                              item
                              xs={3}
                              className="event-organization__information__headline-entry"
                            >
                              <EntryWithHeadline
                                entry={
                                  eventData.isParticipantListVisible
                                    ? t(translations.common.yes())
                                    : t(translations.common.no())
                                }
                                headline={`${t(
                                  translations.pages.events.create.partListVisible()
                                )}:`}
                              />
                            </Grid>
                            <Grid
                              item
                              xs={3}
                              className="event-organization__information__headline-entry"
                            >
                              <EntryWithHeadline
                                entry={
                                  eventData.hasSameGroupSize
                                    ? t(translations.common.yes())
                                    : t(translations.common.no())
                                }
                                headline={`${t(translations.pages.events.create.sameGroupSize())}:`}
                              />
                            </Grid>
                            <Grid
                              item
                              xs={3}
                              className="event-organization__information__headline-entry"
                            >
                              <EntryWithHeadline
                                entry={t(
                                  translations.pages.events.eventDetailPage.waitingListTypes[
                                    eventData.waitingType
                                  ]()
                                )}
                                headline={`${t(translations.pages.events.create.waitingList())}:`}
                              />
                            </Grid>
                            <Grid
                              item
                              xs={3}
                              className="event-organization__information__headline-entry"
                            >
                              <EntryWithHeadline
                                entry={t(
                                  translations.pages.events.eventDetailPage.priorizationType[
                                    eventData.priorizationType
                                  ]()
                                )}
                                headline={`${t(translations.pages.events.create.priorization())}:`}
                              />
                            </Grid>
                          </Grid>
                          {(canEdit || readOnlyAdmin) && (
                            <Grid item>
                              <ParticipantCriteria
                                eventData={eventData}
                                authorRoles={authorRoles}
                              />
                            </Grid>
                          )}
                        </Grid>
                      }
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </Grid>
              {eventData.enrollmentId && (
                <Grid item className="event-organization__information__participantInformation">
                  <Divider />
                  <Grid container>
                    <Grid item xs={4} className="event-organization__information__headline-entry">
                      <EntryWithHeadline
                        entry={
                          status
                            ? t(
                                translations.pages.events.eventDetailPage.participantInfos.participantStates[
                                  status
                                ]()
                              )
                            : ''
                        }
                        headline={
                          isPreCalc(eventData)
                            ? t(
                                translations.pages.events.eventDetailPage.participantInfos.statusPreCalc()
                              )
                            : t(translations.pages.events.eventDetailPage.participantInfos.status())
                        }
                      />
                    </Grid>
                    {position && (
                      <Grid item xs={4} className="event-organization__information__headline-entry">
                        <EntryWithHeadline
                          entry={`${position}`}
                          headline={t(
                            translations.pages.events.eventDetailPage.participantInfos.position()
                          )}
                        />
                      </Grid>
                    )}
                    {group && (
                      <Grid item xs={4} className="event-organization__information__headline-entry">
                        <EntryWithHeadline
                          entry={`${group}`}
                          headline={t(
                            translations.pages.events.eventDetailPage.participantInfos.group()
                          )}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
      </GQLGuard>
    </>
  );
};

interface OrganizerRolesProps {
  eventData: ResolvedEvent;
  authorRoles: any;
}
const OrganizerRoles = ({ eventData, authorRoles }: OrganizerRolesProps) => {
  const { t } = useTranslation();
  const roles = eventData.organizers?.filter(
    organizer => organizer.organizerType === OrganizerType.ROLE
  );
  return (
    <>
      <Typography component={'h3'}>
        {t(translations.pages.events.eventDetailPage.organizingAuthorgroups())}
      </Typography>
      <Grid container justifyContent={'flex-start'} spacing={2}>
        <Grid item>
          {roles?.length
            ? roles.map(organizer => {
                return (
                  <Typography key={organizer.id}>
                    {
                      authorRoles.permissions?.find(
                        per => parseInt(per.authorId, 10) === organizer.refId
                      )?.authorRole?.name
                    }
                  </Typography>
                );
              })
            : t(translations.common.none())}
        </Grid>
      </Grid>
    </>
  );
};

interface OrganizerUserProps {
  userData: any;
}

const OrganizerUsers = ({ userData }: OrganizerUserProps) => {
  const { t } = useTranslation();
  return (
    <>
      <Typography component={'h3'}>
        {t(translations.pages.events.eventDetailPage.organizingUsers())}
      </Typography>
      <Grid container justifyContent={'flex-start'} spacing={2}>
        <Grid item>
          {userData.map(user => (
            <Typography key={user.id}>{user.email}</Typography>
          ))}
        </Grid>
      </Grid>
    </>
  );
};

const removeIDMPrefix = ent => {
  if (ent.startsWith('IDM_')) {
    return ent.slice(4);
  }
  return ent;
};

interface ParticipantCriteriaProps {
  eventData: ResolvedEvent;
  authorRoles: any;
}

const ParticipantCriteria = ({ eventData, authorRoles }: ParticipantCriteriaProps) => {
  const { t } = useTranslation();
  const combinedSemesters =
    eventData.participantCriteria?.combinedSemesters.filter(
      csem => csem !== CombinedSemester.NONE
    ) || [];
  return (
    <Grid container direction={'row'} justifyContent={'space-evenly'} spacing={2}>
      <Grid item xs={6}>
        <Typography component={'h3'}>
          {t(translations.pages.events.eventDetailPage.participatingEntitlements())}:
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography component={'h3'}>
          {t(translations.pages.events.eventDetailPage.participatingSemesters())}:
        </Typography>
      </Grid>
      <Grid item xs>
        <Grid container spacing={1}>
          {eventData.participantCriteria?.entitlements.map(ent => {
            ent = removeIDMPrefix(ent);
            return (
              <Grid item key={ent}>
                <Chip variant="outlined" label={ent} />
              </Grid>
            );
          })}
        </Grid>
      </Grid>
      <Grid item>
        <Divider orientation="vertical" />
      </Grid>
      <Grid item xs>
        <Grid container spacing={1}>
          {combinedSemesters.length !== 0 &&
            combinedSemesters.map(sem => (
              <Grid item key={sem}>
                <Chip variant="outlined" label={sem} />
              </Grid>
            ))}
        </Grid>
      </Grid>
    </Grid>
  );
};
